<script setup lang="ts">
import { computed } from 'vue'
import { mdiPlus, mdiMinus } from '@mdi/js'
import { useEditorStore } from '@/stores/Editor'
import { BackgroundTheme } from '@/api/interfaces'
import { getImageUrl } from '@/helpers/utils'
import { useDisplay } from 'vuetify'
import { constants } from '@/constants'
import { useRoute } from 'vue-router'

import GreensparkLogo from '@/components/icons/GreensparkLogo.vue'
import PublicFooterMenu from '@/components/public-page/PublicFooterMenu.vue'
import FacebookIcon from '@/components/icons/social-media/FacebookIcon.vue'
import LinkedinIcon from '@/components/icons/social-media/LinkedinIcon.vue'
import InstagramIcon from '@/components/icons/social-media/InstagramIcon.vue'

const { smAndDown: isMobile } = useDisplay()
const editorStore = useEditorStore()
const currentYear = new Date().getFullYear()
const route = useRoute()

const background = computed(() => {
  if (editorStore.editor.background.theme === BackgroundTheme.DARK) {
    return getImageUrl('/src/assets/images/backgrounds/footer-bg-dark.svg')
  }

  if (
    editorStore.editor.background.theme === BackgroundTheme.CUSTOM &&
    route.name === 'CommunityPage'
  ) {
    return getImageUrl('/src/assets/images/backgrounds/footer-bg-custom.svg')
  }

  return getImageUrl('/src/assets/images/backgrounds/footer-bg.svg')
})

const logoColor = computed(() => {
  if (
    editorStore.editor.background.theme === BackgroundTheme.DARK ||
    (editorStore.editor.background.theme === BackgroundTheme.CUSTOM &&
      route.name === 'CommunityPage')
  ) {
    return 'white'
  }

  return 'black'
})

const socialIcons = {
  linkedin: LinkedinIcon,
  instagram: InstagramIcon,
  facebook: FacebookIcon,
}

const footerLinks = {
  company: [
    {
      title: 'PublicFooter.links.about',
      link: 'https://www.getgreenspark.com/about-us',
    },
    {
      title: 'PublicFooter.links.projects',
      link: 'https://www.getgreenspark.com/projects',
    },
    {
      title: 'PublicFooter.links.case_studies',
      link: 'https://www.getgreenspark.com/case-studies',
    },
    {
      title: 'PublicFooter.links.journal',
      link: 'https://www.getgreenspark.com/journal',
    },
  ],
  contact: [
    {
      title: 'PublicFooter.links.book_demo',
      link: 'https://www.getgreenspark.com/demo/book-a-demo',
    },
    {
      title: 'PublicFooter.links.email',
      link: 'mailto:hello@getgreenspark.com',
    },
  ],
  legal: [
    {
      title: 'PublicFooter.links.terms_and_conditions',
      link: 'https://www.getgreenspark.com/utility/legal',
    },
    {
      title: 'PublicFooter.links.privacy_policy',
      link: 'https://app.privasee.io/privacy-portal/61a7587adcff740014657fbf?theme=light',
    },
  ],
}
</script>

<template>
  <footer class="public-footer">
    <img class="public-footer-image" :src="background" alt="Greenspark background" loading="lazy" />
    <div class="public-footer-content">
      <v-container>
        <div class="public-footer-wrapper">
          <div>
            <div class="public-footer-cta">
              <p>{{ $t('PublicFooter.climate_hero.title') }}</p>
              <GsButton
                type="success"
                href="https://www.getgreenspark.com/account-type"
                target="_blank"
              >
                {{ $t('PublicFooter.climate_hero.cta') }}
              </GsButton>
            </div>
            <v-expansion-panels v-if="isMobile" class="public-footer-menu-accordion" flat tile>
              <v-expansion-panel
                v-for="(links, key) in footerLinks"
                :key="key"
                class="public-footer-menu-accordion-item"
              >
                <v-expansion-panel-title
                  class="public-footer-menu-accordion-item-title"
                  :expand-icon="mdiPlus"
                  :collapse-icon="mdiMinus"
                >
                  {{ $t(`PublicFooter.menus.${key}`) }}
                </v-expansion-panel-title>
                <v-expansion-panel-text class="public-footer-menu-accordion-item-content">
                  <PublicFooterMenu :menu-items="links" />
                </v-expansion-panel-text>
              </v-expansion-panel>
            </v-expansion-panels>
            <GreensparkLogo class="logo" :color="logoColor" />
            <small class="copyright">
              {{ $t('PublicFooter.copyright', { currentYear }) }}
            </small>
          </div>
          <div v-if="!isMobile" class="public-footer-menu">
            <div v-for="(links, key) in footerLinks" :key="key" class="public-footer-menu-item">
              <p class="public-footer-menu-item-title">
                <strong>{{ $t(`PublicFooter.menus.${key}`) }}</strong>
              </p>
              <PublicFooterMenu :menu-items="links" />
            </div>
          </div>
        </div>
        <div class="public-footer-social-links">
          <a
            v-for="(icon, key) in socialIcons"
            :key="key"
            :href="constants.socialMediaLinks[key]"
            target="_blank"
          >
            <component
              class="public-footer-social-icon"
              :is="icon"
              :color="editorStore.editor.hero.image.textColor"
            />
          </a>
        </div>
      </v-container>
    </div>
  </footer>
</template>

<style lang="scss" scoped>
.public-footer-content {
  background-color: var(--gray-light-f1);
  color: var(--ui-black);
  padding: 45px 0;
  margin-top: -5px;

  @include media('md-and-up') {
    padding: 75px 0;
  }

  .dark & {
    background-color: #1a1a1a;
    color: var(--lightest);
  }

  .community-page & {
    background-color: #1b4543;
    color: var(--lightest);
  }
}

.public-footer-image {
  width: 100%;
  object-fit: cover;
}

.public-footer-wrapper {
  display: flex;
  flex-direction: column;
  margin: 0 15px;

  @include media('md-and-up') {
    justify-content: space-between;
    flex-direction: row;
    margin: 0;
  }
}

.public-footer-cta {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  margin-bottom: 35px;

  @include media('md-and-up') {
    display: block;
    text-align: left;
    margin-bottom: 70px;
  }

  p {
    font-size: 26px;
    font-weight: 400;
    line-height: 24px;
    margin-bottom: 16px;
  }
}

.public-footer-menu {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 25px;
  order: -1;
  margin-bottom: 50px;

  @include media('md-and-up') {
    gap: 45px;
    flex-direction: row;
    order: 1;
    margin-bottom: 0;
    align-items: flex-start;
    text-align: left;
  }
}

.public-footer-menu-item {
  @include media('md-and-up') {
    width: 150px;
  }
}

.public-footer-menu-item-title,
.public-footer-menu-item-links a {
  font-size: 18px;
  line-height: 24px;
  color: var(--ui-black);

  .dark & {
    color: var(--lightest);
  }

  .community-page & {
    color: var(--lightest);
  }
}

.public-footer-menu-item-links {
  list-style-type: none;

  a {
    display: inline-block;
    text-decoration: none;
    margin-top: 12px;

    &:hover {
      color: var(--ui-emerald-green);
    }
  }
}

.logo {
  @include media('sm-and-down') {
    display: flex;
    margin: 0 auto;
  }
}

.copyright {
  display: block;
  font-size: 14px;
  font-weight: 400;
  line-height: 24px;
  margin-top: 6px;
  text-align: center;

  @include media('md-and-up') {
    margin-top: 16px;
    text-align: left;
  }
}

.public-footer-menu-accordion {
  max-width: 500px;
  margin: 0 auto 45px;
}

.public-footer-menu-accordion-item {
  background-color: transparent;
  margin-top: 0 !important;

  &:not(:last-child) {
    border-bottom: 1px solid var(--gray-light-cc);
  }

  :deep(.v-expansion-panel-text__wrapper) {
    padding: 0 12px 24px;
  }
}

.public-footer-menu-accordion-item-title {
  font-size: 20px;
  font-weight: 700;
  letter-spacing: 0.4px;
  text-transform: uppercase;
  padding: 24px 0;

  .dark & {
    color: var(--lightest);
  }

  .community-page & {
    color: var(--lightest);
  }

  &:hover > :deep(.v-expansion-panel-title__overlay) {
    opacity: 0;
  }
}

.public-footer-social-links {
  display: flex;
  justify-content: center;
  gap: 24px;
  margin-top: 24px;

  @include media('md-and-up') {
    margin-top: 0;
    justify-content: flex-end;
  }
}

.public-footer-social-icon {
  width: 16px;
}
</style>
